import { useMemo, useState } from "react";
import type { ReconResult } from "../types/api";
import { api } from "../api/client";
import { cellText, fmtCell } from "../lib/columns";
import { releaseKeys } from "../lib/clearedRelease";
import { useGridSelection } from "./useGridSelection";
import { useColumnWidths } from "./useColumnWidths";

interface Props {
  result: ReconResult;
  // Release an app-made pairing (it is remembered, and restorable from Manual Match's
  // Released matches), then re-run the finalize. Absent: the tab stays read-only.
  insurerName?: string;
  onChanged?: () => void;
}

export interface ClearedPair {
  in_window_side: "cri" | "insurer";
  in_window_index: number;
  clearing_side: "cri" | "insurer";
  clearing_index: number;
  key: string;
  amount: number;
  track: string;
  section?: "in_period" | "before_period" | "cleared_before_period";
  cleared_in?: string;
  // A hand group (backend/manual_cleared.py): one pair per open leg, all naming every
  // leg that cleared the group.
  group?: string;
  clearing_legs?: ["cri" | "insurer", number][];
  // "manual" for a hand-routed pairing; the app's own name the key they joined on.
  evidence?: string;
}

// One display row: the open item on the left, what settled it on the right. Amounts are
// unknown (real numbers from the frame), never pre-formatted strings — see `raw` below.
// A hand group's rows are ragged, so either half may be empty ("" side).
interface ClearedRow {
  key: string;
  track: string;
  amount: number;
  section: string;
  clearedIn: string;
  inSide: "cri" | "insurer" | "";
  inRef: string;
  inDate: string;
  inAmt: unknown;
  clSide: "cri" | "insurer" | "";
  clRef: string;
  clDate: string;
  clAmt: unknown;
  // The open row's _sig when the app made this pairing (lib/clearedRelease), else "".
  releaseSig: string;
}

// Three populations, mirroring reporting/cleared_rows.py::_CLEARED_SECTIONS: normal period
// activity that settled late; prior-period items the run took off Group 1/2 for the
// Cleared line; and opening-balance items no line counts. None moves the Difference.
const SECTIONS = [
  {
    id: "in_period",
    title: "Open during the period — cleared after period end",
    blurb:
      "Dated inside the reconciliation period and still open at period end, settled by a transaction dated after it.",
  },
  {
    id: "prior_period_cleared",
    title: "Prior-period items — cleared after period end",
    blurb:
      "Carried in from the previous period's unreconciled items and still open at period end, settled by a transaction dated after it. Taken off Group 1 / Group 2 and counted on the Overview's Cleared in Subsequent Month line. Marked “[Open before period]” in the note on the exported statement.",
  },
  {
    id: "before_period",
    title: "Open before the period — cleared after period end",
    blurb:
      "Already open when the period began and sitting in the opening balance. Listed here for information only and not counted on the Cleared line. Where a prior-period copy of one could not be matched to it one-to-one, that copy stays on Group 1 / Group 2 and is counted there instead. Marked “[Open before period]” in the note on the exported statement.",
  },
] as const;

// The engine's own proposal sections this tab lists; "prior_period_cleared" is a display
// split of before_period, decided by the backend's cleared_after_rows.
const PROPOSAL_SECTIONS = new Set(["in_period", "before_period"]);

// The pairs this tab lists. The settled-before-the-period population is excluded here
// and on the Excel sheet alike (reporting/cleared_rows.py::_cleared_sheet_payload): it
// cleared EARLIER, which contradicts the tab's title, and it is already stated on the
// Matched tab once the prior period is uploaded, or on Group 1 / Group 2 otherwise
// (finance 2026-09-16). The note naming the settling document stays on the row.
//
// Exported because the tab's PRESENCE and count are decided by its caller: reading the
// raw proposals there put a "Cleared in Subsequent Month (12)" tab on a run whose every
// proposal was settled-before, opening onto nothing.
export function clearedTabPairs(result: ReconResult): ClearedPair[] {
  return ((result.cleared_subsequent as ClearedPair[] | undefined) ?? []).filter(
    (p) => PROPOSAL_SECTIONS.has(p.section ?? "in_period"));
}

// Items the tab lists: the pairs above plus carried items a later row cleared, which have
// no ledger row of their own (backend carried_cleared_after) and so are not proposals.
export function clearedTabCount(result: ReconResult): number {
  return clearedTabPairs(result).length
    + (result.unreconciled?.cleared_after_items?.length ?? 0);
}

// Cleared in Subsequent Month — items dated inside the reconciliation period whose
// clearing transaction is dated AFTER the period end. Paired CRI-left | insurer-right
// so the reviewer can see the item and what settled it side by side.
//
// Math-inert by design: the in-window row keeps its own bucket and stays unmatched, and
// the clearing row is outside the window so it is counted in no balance, bucket or count.
// Nothing on this tab moves the Difference. Its one action, Release on an app-made
// pairing (lib/clearedRelease), is stored like any release and applied by the engine.
export default function ClearedSubsequentTab({ result, insurerName, onChanged }: Props) {
  const pairs = useMemo(() => clearedTabPairs(result), [result]);
  const keys = useMemo(() => releaseKeys(result), [result]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const canRelease = Boolean(insurerName && onChanged);
  async function release(sig: string) {
    setBusy(sig);
    setError("");
    try {
      await api.releaseMatch(insurerName!, [sig], "released from Cleared in Subsequent Month");
      onChanged!();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  const rows = useMemo(() => {
    const pick = (side: string) => (side === "insurer" ? result.df_insurer : result.df_cri);
    // Raw cell — value AS THE FRAME HOLDS IT. Amounts must stay real numbers all the way
    // to the <td>: stringifying them here (which this helper used to do for every column
    // alike) makes the selection aggregate treat money as text, and re-parsing a display
    // string cannot safely tell an amount from a numeric-looking reference.
    const raw = (side: string, idx: number, col: string): unknown => {
      const f = pick(side);
      if (!f) return null;
      const ci = f.columns.indexOf(col);
      if (ci < 0) return null;
      const ri = f.indices.indexOf(idx);
      return ri < 0 ? null : f.rows[ri][ci];
    };
    const cell = (side: string, idx: number, col: string) => {
      const v = raw(side, idx, col);
      return v === null || v === undefined ? "" : String(v);
    };
    const amountCol = (side: string) => (side === "insurer" ? "balance_amount" : "cri_amount");
    const refCol = (side: string) => (side === "insurer" ? "doc_reference" : "reference_no");

    const moved = new Set((result.unreconciled?.cleared_after_rows ?? [])
      .map(([book, row]) => `${book}:${row}`));
    const sectionOf = (p: ClearedPair) => {
      const s = p.section ?? "in_period";
      return s === "before_period" && moved.has(`${p.in_window_side}:${p.in_window_index}`)
        ? "prior_period_cleared" : s;
    };

    type Leg = ["cri" | "insurer", number];
    const row = (p: ClearedPair, open?: Leg, clear?: Leg): ClearedRow => ({
      key: p.key,
      track: p.track,
      amount: p.amount,
      section: sectionOf(p),
      clearedIn: clear ? p.cleared_in ?? "" : "",
      inSide: open ? open[0] : "",
      inRef: open ? cell(open[0], open[1], refCol(open[0])) || p.key : "",
      inDate: open ? cell(open[0], open[1], "doc_date") : "",
      inAmt: open ? raw(open[0], open[1], amountCol(open[0])) : null,
      clSide: clear ? clear[0] : "",
      clRef: clear ? cell(clear[0], clear[1], refCol(clear[0])) || p.key : "",
      clDate: clear ? cell(clear[0], clear[1], "doc_date") : "",
      clAmt: clear ? raw(clear[0], clear[1], amountCol(clear[0])) : null,
      releaseSig: open && clear ? keys.get(`${open[0]}:${open[1]}`) ?? "" : "",
    });
    // A hand group lists once per section, where its first open leg falls.
    const groups = new Map<string, ClearedPair[]>();
    for (const p of pairs) {
      if (p.group == null) continue;
      const k = `${sectionOf(p)}|${p.group}`;
      groups.set(k, [...(groups.get(k) ?? []), p]);
    }
    const out: ClearedRow[] = [];
    for (const p of pairs) {
      if (p.group == null) {
        out.push(row(p, [p.in_window_side, p.in_window_index],
                     [p.clearing_side, p.clearing_index]));
        continue;
      }
      const members = groups.get(`${sectionOf(p)}|${p.group}`) ?? [];
      if (members[0] !== p) continue;
      const clears: Leg[] = p.clearing_legs ?? [[p.clearing_side, p.clearing_index]];
      for (let i = 0; i < Math.max(members.length, clears.length); i++) {
        const m = members[i];
        out.push(row(m ?? p, m ? [m.in_window_side, m.in_window_index] : undefined, clears[i]));
      }
    }
    // Carried items cleared by a later row: the open leg is finance's own list row.
    const unrec = result.unreconciled;
    for (const x of unrec?.cleared_after_items ?? []) {
      const f = x.book === "insurer" ? unrec?.rows : unrec?.cri_rows;
      const ri = f ? f.indices.indexOf(x.item) : -1;
      const at = (col: string): unknown => {
        const ci = f && ri >= 0 ? f.columns.indexOf(col) : -1;
        return ci < 0 ? null : f!.rows[ri][ci];
      };
      const txt = (col: string) => { const v = at(col); return v === null || v === undefined ? "" : String(v); };
      // A hand pair may clear with several rows: the item once, then one line per row.
      const clears: Leg[] = x.clearing_legs ?? [[x.clearing_side, x.clearing_index]];
      clears.forEach((clear, i) => out.push({
        key: i ? `carried:${x.book}:${x.item}:${i}` : `carried:${x.book}:${x.item}`,
        track: x.track, amount: x.amount,
        section: "prior_period_cleared", clearedIn: x.cleared_in,
        inSide: i ? "" : x.book,
        inRef: i ? "" : txt(x.book === "insurer" ? "doc_reference" : "reference_no"),
        inDate: i ? "" : txt("doc_date"),
        inAmt: i ? null : x.amount,
        clSide: clear[0], clRef: cell(clear[0], clear[1], refCol(clear[0])),
        clDate: cell(clear[0], clear[1], "doc_date"),
        clAmt: raw(clear[0], clear[1], amountCol(clear[0])),
        releaseSig: "",
      }));
    }
    return out;
  }, [pairs, keys, result.df_cri, result.df_insurer, result.unreconciled]);

  if (!rows.length) {
    return (
      <div>
        <p className="muted">
          No items were cleared in a subsequent month — nothing in the selected period
          has a matching transaction dated after the period end.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="muted" style={{ margin: "0 0 14px" }}>
        Open items whose clearing transaction is dated <strong>after the period end</strong>.
        Items open during the period, and prior-period items carried in from the previous
        period, are taken off Group 1 / Group 2 and counted on the Overview's own Cleared in
        Subsequent Month line; they carry into next month when this reconciliation's export
        is uploaded as the previous month's output. Other items already open before the
        period are listed here for information only and are not counted on the Cleared
        line; where a prior-period copy of one could not be matched to it one-to-one, that
        copy stays on Group 1 / Group 2 and is counted there instead. The clearing row is
        outside the period so it is counted in no balance or total.{" "}
        <strong>Nothing here affects the Difference.</strong>
        {canRelease && " If the app paired an item with the wrong later transaction, Release undoes that pairing: the item returns to Group 1 / Group 2 as open at period end, and the decision is remembered (restore it from Manual Match's Released matches)."}
      </p>
      {error && <p className="error">Could not release: {error}</p>}

      {SECTIONS.map((sec) => {
        const secRows = rows.filter((r) => r.section === sec.id);
        if (!secRows.length) return null;
        return (
          <div key={sec.id} style={{ marginBottom: 18 }}>
            <h4 style={{ margin: "0 0 2px" }}>
              {sec.title} ({secRows.filter((r) => r.inSide).length})
            </h4>
            <p className="muted" style={{ margin: "0 0 8px", fontSize: "0.9em" }}>
              {sec.blurb}
            </p>
            <SectionTable secRows={secRows} busy={busy}
              onRelease={canRelease ? release : undefined} />
          </div>
        );
      })}
    </div>
  );
}

const sideLabel = (s: ClearedRow["inSide"]) => (s === "insurer" ? "Insurer" : s ? "CRI" : "");

// One section's table. A component rather than inline JSX because each section carries
// its OWN cell selection, and a hook cannot be called inside the SECTIONS map.
function SectionTable({ secRows, busy, onRelease }: {
  secRows: ClearedRow[]; busy: string; onRelease?: (sig: string) => void;
}) {
  const undo = Boolean(onRelease) && secRows.some((r) => r.releaseSig);
  // This tab is built from pair objects, not a FrameRecords, so flatten to the row-array
  // shape the selection hook takes. Amounts stay NUMBERS (see `raw` above) so they foot
  // like every other grid; the identity columns are text and are reported as ignored.
  const selRows = useMemo<unknown[][]>(
    () => secRows.map((r) => [
      sideLabel(r.inSide), r.inRef, r.inDate, r.inAmt,
      r.clearedIn, sideLabel(r.clSide), r.clRef, r.clDate,
    ]),
    [secRows],
  );
  const labels = useMemo(
    () => ["Side", "Reference", "Date", "Amount",
           "Cleared in", "Side", "Reference", "Date"],
    [],
  );
  const colIdx = useMemo(() => labels.map((_, i) => i), [labels]);
  // Column positions in the flattened row above. Named because the renderer keys date
  // formatting and the bold clearance month off them.
  const AMOUNT = 3, CLEARED_IN = 4, DATE_COLS = [2, 7];
  // The pair key identifies the row; a bare index would let a selection survive onto a
  // different pair when the section's contents change.
  const ids = useMemo(() => secRows.map((r, i) => `${r.key}#${i}`), [secRows]);
  const sel = useGridSelection(selRows, colIdx, labels, ids);
  const cw = useColumnWidths(
    useMemo(() => [...labels.map((_, i) => String(i)), ...(undo ? ["__undo"] : [])], [labels, undo]),
    secRows);

  return (
    <div {...sel.gridProps}>
      <div ref={(el) => { sel.scrollHostRef.current = el; }} style={{ overflowX: "auto" }}>
        <table className={"data-table" + cw.className} style={cw.style}>
          {cw.colgroup}
          <thead>
            <tr>
              <th colSpan={4} style={{ textAlign: "left" }}>Open item</th>
              <th colSpan={4} style={{ textAlign: "left" }}>
                Cleared by — after period end
              </th>
              {undo && <th rowSpan={2} />}
            </tr>
            <tr>
              {labels.map((label, ci) => (
                <th key={ci} className={sel.isColSelected(ci) ? "col-selected" : undefined}
                  style={ci === AMOUNT ? { textAlign: "right" } : undefined}>
                  <span className="th-label" title="Click to total this column · Ctrl+click to add another"
                    onClick={(e) => { sel.onHeaderClick(ci, e); sel.focusGrid(); }}>
                    {label}
                  </span>
                  {cw.grip(String(ci))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {secRows.map((_r, ri) => (
              <tr key={ri}>
                {selRows[ri].map((v, ci) => (
                  <td key={ci}
                    className={[
                      typeof v === "number" ? "num" : "",
                      sel.isSelected(ri, ci) ? "cell-selected" : "",
                    ].filter(Boolean).join(" ")}
                    data-row={ri} data-col={ci}
                    onMouseDown={(e) => { sel.onCellMouseDown(ri, ci, e); sel.focusGrid(); }}
                    onMouseEnter={() => sel.onCellMouseEnter(ri, ci)}>
                    {ci === CLEARED_IN
                      ? <strong>{fmtCell(v, "")}</strong>
                      : cellText(v, ci === AMOUNT ? "amount" : DATE_COLS.includes(ci) ? "doc_date" : "")}
                  </td>
                ))}
                {undo && (
                  <td>
                    {secRows[ri].releaseSig && (
                      <button className="btn btn-sm" disabled={busy !== ""}
                        title="The app paired these two. Release undoes the pairing: the item is open at period end again (Group 1 / Group 2), the later row returns to its own list, and the decision is remembered."
                        onClick={() => onRelease!(secRows[ri].releaseSig)}>
                        {busy === secRows[ri].releaseSig ? "Releasing…" : "Release"}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel.statusBar}
    </div>
  );
}
